import { useMemo } from 'react';
import { LODGING_INFO } from '../util/package';
import { CAMPSITES_TYPE } from '../util/campsite';
import {
  buildCabinFeatures,
  buildHouseFeatures,
  buildTentFeatures,
  buildRVFeatures,
} from '../util/lodging';

const useLodgingFeatures = ({
  campsites,
  lodging,
  lodgingPricesOptions,
  intl,
  filterBy = 'required',
}) => {
  const features = useMemo(() => {
    if (!campsites || !campsites.length) return [];

    const result = [];
    const isOptional = filterBy === 'optional';

    const lodgingTents =
      lodgingPricesOptions?.filter(item => item.type === CAMPSITES_TYPE.TENT) || [];
    const lodgingRVs = lodgingPricesOptions?.filter(item => item.type === CAMPSITES_TYPE.RV) || [];

    const cabinOption = lodgingPricesOptions?.find(item => item.type === 'cabin');
    const houseOption = lodgingPricesOptions?.find(item => item.type === 'house');

    const isCabinMatch =
      cabinOption &&
      (isOptional ? cabinOption.option === 'optional' : cabinOption.option === 'required');
    const isHouseMatch =
      houseOption &&
      (isOptional ? houseOption.option === 'optional' : houseOption.option === 'required');

    // Handle Cabin
    if (isCabinMatch && lodging?.cabin) {
      const cabinFeatures = buildCabinFeatures(
        lodging.cabin,
        lodging,
        cabinOption,
        intl,
        isOptional
      );

      if (cabinFeatures) {
        // Both optional and required now return section with heading
        result.push(cabinFeatures);
      }
    }

    // Handle House
    if (isHouseMatch && lodging?.house) {
      const houseFeatures = buildHouseFeatures(
        lodging.house,
        lodging,
        houseOption,
        intl,
        isOptional
      );

      if (houseFeatures) {
        // Both optional and required now return section with heading
        result.push(houseFeatures);
      }
    }

    // Handle Tent Campsites
    campsites.forEach((campsite, index) => {
      if (campsite.allowsTents && lodgingTents.length > 0 && lodgingTents[index]) {
        const matchesFilter = isOptional
          ? lodgingTents[index].option === LODGING_INFO.OPTIONAL
          : lodgingTents[index].option === LODGING_INFO.REQUIRED;

        if (matchesFilter) {
          const tentFeatures = buildTentFeatures(campsite, lodgingTents[index], intl, isOptional);

          if (tentFeatures) {
            // Both optional and required now return section with heading
            result.push(tentFeatures);
          }
        }
      }
    });

    // Handle RV Campsites
    let rvIndex = 1;
    const hasSingleRVSite = lodgingRVs.length === 1;

    campsites.forEach((campsite, index) => {
      if (campsite?.allowsRvs && lodgingRVs.length > 0 && lodgingRVs[index]?.option) {
        const matchesFilter = isOptional
          ? lodgingRVs[index].option === LODGING_INFO.OPTIONAL
          : lodgingRVs[index].option === LODGING_INFO.REQUIRED;

        if (matchesFilter) {
          const rvFeatures = buildRVFeatures(
            campsite,
            lodgingRVs[index],
            intl,
            isOptional,
            rvIndex,
            hasSingleRVSite
          );

          if (rvFeatures) {
            // Both optional and required now return section with heading
            result.push(rvFeatures);
          }

          rvIndex += 1;
        }
      }
    });

    // Return the result directly (both required and optional now have headings)
    return result;
  }, [campsites, lodging, lodgingPricesOptions, intl, filterBy]);

  return { features };
};

export default useLodgingFeatures;
